import { useEffect, useMemo, useState } from 'react'
import { Logger } from '@/services/Logger'
import { Oval as LoadingSpinner } from 'react-loader-spinner'
import './UserManagementPage.css'
import { EDIT_USER, CREATE_USER } from '@/constants/forms'
import { PAGES } from '@/constants/pages'
import PermissionGuard from '@/components/PermissionGuard'
import { authService, PermissionScope } from '@/services/AuthService'
import { RESOURCES } from '@/constants/resources'
import { UserTable } from '@/components/UserTable'
import { User } from '@/models/user'
import { NewUserDto, PartnerUserService, UserDto } from '@/services/PartnerUserService'
import { getImpersonatedPartnerId, usePartner } from '@/contexts/partnerContext'
import { PageLayout } from './PageLayout'
import { PartnerRoleService } from '@/services/PartnerRoleService'
import toast from '@/utils/toast'; 
import { useLoading } from '@/contexts/LoadingContext'

export default function UserManagementPage() {
  const { showLoader, hideLoader } = useLoading()
  const [users, setUsers] = useState<User[]>([])
  const [roles, setRoles] = useState<string[]>([])
  const { impersonatedPartnerId } = usePartner();

  const shouldGetUsers = impersonatedPartnerId !== null || authService.channelPartnerId !== null;

  const fetchUsers = async () => {
    try {
      if (!shouldGetUsers) {
        setUsers([]);
        return;
      };

      showLoader()
      const result = await PartnerUserService.getAll();
      if (result.users) {
        setUsers([...result.users]);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      hideLoader()
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [impersonatedPartnerId]); 

  // Create modal state
  const [createModal, setCreateModal] = useState({
    show: false,
    loading: false,
    form: { channelPartnerId: 0, role: 'Partner Read Only', firstName: '', lastName: '', email: '' } as NewUserDto,
    errors: {} as { [key: string]: string }
  })

  // Edit modal state
  const [editModal, setEditModal] = useState({
    show: false,
    loading: false,
    userId: '',
    email: '',
    firstName: '',
    lastName: '',
    role: '',
    errors: {} as { [key: string]: string }
  })


  // Validation functions
  const validateCreateForm = (form: NewUserDto): { [key: string]: string } => {
    const errors: { [key: string]: string } = {}
    
    if (!form.email?.trim()) {
      errors.email = 'Email is required'
    } else if (form.email && form.email.length > 200) {
      errors.email = 'Email must be 200 characters or less'
    } else if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = 'Email must be a valid email address'
    } 

    if (!errors.email && form.email) {
      const isDuplicate = users.some(user => {
        const emailMatches = user.email.toLowerCase() === form.email!.toLowerCase(); 
        return emailMatches;
      });

      if (isDuplicate) {
        errors.email = 'Email already exists.'
      }
    }
    
    if (!form.firstName?.trim()) {
      errors.firstName = 'First name is required'
    } else if (form.firstName && form.firstName.length > 100) {
      errors.firstName = 'First name must be 100 characters or less'
    }

    if (!form.lastName?.trim()) {
      errors.lastName = 'Last name is required'
    } else if (form.lastName && form.lastName.length > 100) {
      errors.lastName = 'Last name must be 100 characters or less'
    }

    return errors
  }

  const validateEditForm = (firstName: string, lastName: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {}

    if (firstName && firstName.length > 100) {
      errors.firstName = 'First name must be 100 characters or less'
    }

    if (lastName && lastName.length > 100) {
      errors.lastName = 'Last name must be 100 characters or less'
    }

    return errors
  }

  const handleCreateFormChange = (field: keyof NewUserDto, value: string | number) => {
    let updatedForm = { ...createModal.form, [field]: value }
    updatedForm.channelPartnerId = (authService.channelPartnerId ?? impersonatedPartnerId) ?? 0;
    const errors = validateCreateForm(updatedForm)
    setCreateModal(prev => ({
      ...prev,
      form: updatedForm,
      errors: { ...prev.errors, [field]: errors[field] || '' }
    }))
  }

  const handleEditFormChange = (field: 'email' | 'firstName' | 'lastName' | 'role', value: string) => {
    const errors = validateEditForm(
      field === 'firstName' ? value : editModal.firstName,
      field === 'lastName' ? value : editModal.lastName
    )
    setEditModal(prev => ({
      ...prev,
      [field]: value,
      errors: { ...prev.errors, [field]: errors[field] || '' }
    }))
  }

  useEffect(() => {
    let active = true;
    const fetchRoles = async () => {
      try {
        const result = (await PartnerRoleService.getAll()).roles;
        if (active) {
          setRoles(result);
        }
      } catch (err) {
        console.error(err);
      } 
    };
  
    fetchRoles();
  
    return () => {
      active = false;
    };
  }, []);

  const openEditModal = (user) => {
    setEditModal({
      show: true,
      loading: false,
      userId: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      errors: {}
    });
  };

  const getInitialCreateForm = useMemo(() => () => {
    return {
      channelPartnerId: (authService.channelPartnerId ?? impersonatedPartnerId) ?? 0,
      role: roles[0],
      firstName: '',
      lastName: '',
      email: ''
    }
  }, [roles])

  const handleOpenCreateModal = () => {
    setCreateModal({
      show: true,
      loading: false,
      form: getInitialCreateForm(),
      errors: {}
    })
    createModal.form.role = roles[0];
  }
  async function submitCreate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const errors = validateCreateForm(createModal.form)
    setCreateModal(prev => ({ ...prev, errors }))

    if (Object.keys(errors).length > 0) {
      return
    }

    setCreateModal(prev => ({ ...prev, loading: true }))
    try {
      await PartnerUserService.create(createModal.form.firstName!, createModal.form.lastName!, createModal.form.email!, createModal.form.role);
      setCreateModal({
        show: false,
        loading: false,
        form: getInitialCreateForm(),
        errors: {}
      })
      fetchUsers();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to create user.');
      setCreateModal(prev => ({ ...prev, loading: false }))
    }
  }

  async function submitEdit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const errors = validateEditForm(editModal.firstName, editModal.lastName)
    setEditModal(prev => ({ ...prev, errors }))

    if (Object.keys(errors).length > 0) {
      return
    }

    setEditModal(prev => ({ ...prev, loading: true }))

    try {
      await PartnerUserService.update(editModal.userId, editModal.firstName, editModal.lastName, editModal.email, editModal.role)
      setEditModal({
        show: false,
        loading: false,
        userId: '',
        email: '',
        firstName: '',
        lastName: '',
        role: '',
        errors: {}
      })
      if (getImpersonatedPartnerId()) {
        setUsers((await PartnerUserService.getAll()).users)
      }
    } catch (error) {
      toast.error((error as Error).message || 'Failed to update user.');
      setEditModal(prev => ({ ...prev, loading: false }))
    }

    const userId = authService.parsedToken?.sub;
    const email = authService.parsedToken?.email;
    if(editModal.userId === userId && email !== editModal.email) {
      authService.logout();
      Logger.info('UserManagementPage: Current user email changed, logging out to refresh token', { userId, newEmail: editModal.email });
    }
  }

  async function onDeleteUser(user: UserDto) {
    if (!confirm(`Are you sure you want to delete ${user.email}?`)) return
    try {
      await PartnerUserService.delete(user.id)
      toast.success('User has successfully been deleted.');
      if (getImpersonatedPartnerId()) {
        setUsers((await PartnerUserService.getAll()).users)
      }
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete user.');
    }
  }

  return (
    <PageLayout
      title={PAGES.USERS.title}
      heading={PAGES.USERS.title}
      action={
        shouldGetUsers && (
          <PermissionGuard
            resource={[RESOURCES.PARTNER_USERS]}
            scope={PermissionScope.CREATE}
          >
            <button className="create-user-btn" onClick={handleOpenCreateModal}>
              Create User
            </button>
          </PermissionGuard>
        )
      }
    >
      <div className="users-table-container">
        <div className="user-list">
          {users == null || users.length === 0 ? (
            <div className="empty-state">
              <p>No users have been found.</p>
            </div>
          ) : (
            <UserTable
                users={users}
                onEdit={openEditModal}
                onDelete={onDeleteUser}
              />
          )}
        </div>
      </div>
  
      {createModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{CREATE_USER.TITLE}</h3>
            <form onSubmit={submitCreate}>
              <div className="form-field">
                <span>{CREATE_USER.FIELDS.EMAIL.LABEL}</span>
                <input
                  placeholder={CREATE_USER.FIELDS.EMAIL.PLACEHOLDER}
                  type={CREATE_USER.FIELDS.EMAIL.TYPE}
                  value={createModal.form.email}
                  onChange={(e) =>
                    handleCreateFormChange("email", e.target.value)
                  }
                  className={createModal.errors.email ? "error" : ""}
                />
                {createModal.errors.email && (
                  <div className="error-message">{createModal.errors.email}</div>
                )}
              </div>
  
              <div className="form-field">
                <span>{CREATE_USER.FIELDS.FIRSTNAME.LABEL}</span>
                <input
                  placeholder={CREATE_USER.FIELDS.FIRSTNAME.PLACEHOLDER}
                  type={CREATE_USER.FIELDS.FIRSTNAME.TYPE}
                  value={createModal.form.firstName}
                  onChange={(e) =>
                    handleCreateFormChange("firstName", e.target.value)
                  }
                  className={createModal.errors.firstName ? "error" : ""}
                />
                {createModal.errors.firstName && (
                  <div className="error-message">
                    {createModal.errors.firstName}
                  </div>
                )}
              </div>
  
              <div className="form-field">
                <span>{CREATE_USER.FIELDS.LASTNAME.LABEL}</span>
                <input
                  placeholder={CREATE_USER.FIELDS.LASTNAME.PLACEHOLDER}
                  type={CREATE_USER.FIELDS.LASTNAME.TYPE}
                  value={createModal.form.lastName}
                  onChange={(e) =>
                    handleCreateFormChange("lastName", e.target.value)
                  }
                  className={createModal.errors.lastName ? "error" : ""}
                />
                {createModal.errors.lastName && (
                  <div className="error-message">
                    {createModal.errors.lastName}
                  </div>
                )}
              </div>
  
              <div className="form-field">
                <span>{CREATE_USER.FIELDS.ROLE.LABEL}</span>
                <select
                  value={createModal.form.role}
                  onChange={(e) => handleCreateFormChange("role", e.target.value)}
                >
                  {roles &&
                    roles.map((name) => (
                      <option value={name}>{name}</option>
                    ))}
                </select>
              </div>
  
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() =>
                    setCreateModal({
                      show: false,
                      loading: false,
                      form: getInitialCreateForm(),
                      errors: {},
                    })
                  }
                  disabled={createModal.loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createModal.loading}
                  className={createModal.loading ? "button-loading" : ""}
                >
                  {createModal.loading ? (
                    <>
                      <LoadingSpinner
                        height={16}
                        width={16}
                        color="#ffffff"
                        visible={true}
                        ariaLabel="loading-spinner"
                        secondaryColor="#cccccc"
                        strokeWidth={2}
                        strokeWidthSecondary={2}
                      />
                      Creating...
                    </>
                  ) : (
                    <span>{CREATE_USER.SUBMIT_BUTTON}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
  
      {/* Edit User Modal */}
      {editModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{EDIT_USER.TITLE}</h3>
            <form onSubmit={submitEdit}>
              <div className="form-field">
                <span>{EDIT_USER.FIELDS.EMAIL.LABEL}</span>
                <input
                  placeholder={EDIT_USER.FIELDS.EMAIL.PLACEHOLDER}
                  type={EDIT_USER.FIELDS.EMAIL.TYPE}
                  value={editModal.email}
                  onChange={(e) => handleEditFormChange("email", e.target.value)}
                  className={editModal.errors.email ? "error" : ""}
                />
                {editModal.errors.email && (
                  <div className="error-message">{editModal.errors.email}</div>
                )}
              </div>
  
              <div className="form-field">
                <span>{EDIT_USER.FIELDS.FIRSTNAME.LABEL}</span>
                <input
                  placeholder={EDIT_USER.FIELDS.FIRSTNAME.PLACEHOLDER}
                  type={EDIT_USER.FIELDS.FIRSTNAME.TYPE}
                  value={editModal.firstName}
                  onChange={(e) =>
                    handleEditFormChange("firstName", e.target.value)
                  }
                  className={editModal.errors.firstName ? "error" : ""}
                />
                {editModal.errors.firstName && (
                  <div className="error-message">
                    {editModal.errors.firstName}
                  </div>
                )}
              </div>
  
              <div className="form-field">
                <span>{EDIT_USER.FIELDS.LASTNAME.LABEL}</span>
                <input
                  placeholder={EDIT_USER.FIELDS.LASTNAME.PLACEHOLDER}
                  type={EDIT_USER.FIELDS.LASTNAME.TYPE}
                  value={editModal.lastName}
                  onChange={(e) =>
                    handleEditFormChange("lastName", e.target.value)
                  }
                  className={editModal.errors.lastName ? "error" : ""}
                />
                {editModal.errors.lastName && (
                  <div className="error-message">{editModal.errors.lastName}</div>
                )}
              </div>
  
              <div className="form-field">
                <span>{EDIT_USER.FIELDS.ROLE.LABEL}</span>
                <select
                  value={editModal.role}
                  onChange={(e) =>
                    handleEditFormChange("role", e.target.value)
                  }
                >
                  {roles &&
                    roles.map((name) => (
                      <option value={name}>{name}</option>
                    ))}
                </select>
              </div>
  
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() =>
                    setEditModal({
                      show: false,
                      loading: false,
                      userId: "",
                      email: "",
                      firstName: "",
                      lastName: "",
                      role: "",
                      errors: {},
                    })
                  }
                >
                  Cancel
                </button>
                <button type="submit"
                  disabled={editModal.loading}
                  className={editModal.loading ? "button-loading" : ""}>
                    {editModal.loading ? (
                    <>
                      <LoadingSpinner
                        height={16}
                        width={16}
                        color="#ffffff"
                        visible={true}
                        ariaLabel="loading-spinner"
                        secondaryColor="#cccccc"
                        strokeWidth={2}
                        strokeWidthSecondary={2}
                      />
                      Saving...
                    </>
                  ) : (
                    <span>{EDIT_USER.SUBMIT_BUTTON}</span>
                  )}        
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );  
}
